// Single-column nav affordance: floating "Jump to inputs" / "Jump to
// results" buttons. Track viewport-relative visibility of the two
// columns via IntersectionObserver, toggle each button's visibility
// based on whether the corresponding section is in view, and
// smooth-scroll on click (respecting `prefers-reduced-motion`).
//
// Two-column layout hides the button container entirely via CSS
// (`@media (min-width: 992px)` rule in `_simulation.scss`), so no
// JavaScript gating is needed for that case.
//
// Timing notes. `initJumpButtons()` is called from `app/js/index.tsx`
// on both `DOMContentLoaded` and `shiny:connected`, but at neither
// moment is shiny.router guaranteed to have rendered the simulation
// page's DOM. We poll on a short interval until the required elements
// exist (or give up after ~3s on a page that isn't /simulation).
// Idempotent: bails when the container is already mounted.

const RETRY_INTERVAL_MS = 50;
const MAX_RETRIES = 60; // ~3 seconds

type JumpTarget = "inputs" | "results";

export function initJumpButtons(): void {
  let attempts = 0;
  const tryInit = (): void => {
    const container = document.getElementById("beamm-jump-buttons");
    if (container && container.dataset.mounted === "true") return;

    const inputs = document.querySelector<HTMLElement>(
      ".beamm-simulation-inputs",
    );
    const results = document.querySelector<HTMLElement>(
      ".beamm-simulation-results",
    );
    const jumpInputsBtn = container?.querySelector<HTMLButtonElement>(
      '[data-target="inputs"]',
    );
    const jumpResultsBtn = container?.querySelector<HTMLButtonElement>(
      '[data-target="results"]',
    );

    if (container && inputs && results && jumpInputsBtn && jumpResultsBtn) {
      setup(container, inputs, results, jumpInputsBtn, jumpResultsBtn);
      return;
    }

    if (++attempts < MAX_RETRIES) {
      window.setTimeout(tryInit, RETRY_INTERVAL_MS);
    }
  };
  tryInit();
}

function setup(
  container: HTMLElement,
  inputs: HTMLElement,
  results: HTMLElement,
  jumpInputsBtn: HTMLButtonElement,
  jumpResultsBtn: HTMLButtonElement,
): void {
  container.dataset.mounted = "true";

  const isInView = (element: HTMLElement): boolean => {
    const rect = element.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0;
  };

  const updateButtons = (): void => {
    // Show each button only when its section is out of view. Both visible:
    // nothing to show. Both off-screen (rare): show both so the user can
    // pick either direction. The toggle reads layout *synchronously* via
    // getBoundingClientRect rather than the observer's IntersectionObserver-
    // Entry, so the class state is exact the instant this runs — the
    // observer's first callback is async (scheduled), and a Cypress
    // assertion can race ahead of it; a synchronous read keeps the state
    // deterministic for both the user and the tests (#249).
    jumpInputsBtn.classList.toggle("is-visible", !isInView(inputs));
    jumpResultsBtn.classList.toggle("is-visible", !isInView(results));
  };

  // Compute once synchronously so the initial state is correct on mount
  // (and immediately assertable). Then let an IntersectionObserver drive
  // every subsequent recompute. The observer is what fixes the reported
  // unreliability (#249): a one-shot init + scroll/resize listeners missed
  // two cases — (1) the async React islands render *after* init and grow
  // the document, pushing `results` below the fold with no scroll event to
  // notice; (2) a page that fits the viewport fires no scroll at all. The
  // observer fires whenever a section crosses the viewport edge, covering
  // both. Its callback just calls updateButtons() (synchronous read), so
  // we get the observer's coverage without depending on its entry timing.
  updateButtons();
  const observer = new IntersectionObserver(updateButtons, {
    threshold: [0, 1],
  });
  observer.observe(inputs);
  observer.observe(results);
  // Belt and braces: resize (rotation, devtools) and scroll still nudge a
  // recompute for any browser/timing where the observer lags.
  window.addEventListener("scroll", updateButtons, { passive: true });
  window.addEventListener("resize", updateButtons, { passive: true });

  const reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  const scrollBehavior: ScrollBehavior = reducedMotion ? "auto" : "smooth";

  const scrollTo = (target: JumpTarget): void => {
    const element = target === "inputs" ? inputs : results;
    element.scrollIntoView({ behavior: scrollBehavior, block: "start" });
  };

  jumpInputsBtn.addEventListener("click", () => scrollTo("inputs"));
  jumpResultsBtn.addEventListener("click", () => scrollTo("results"));
}
